export interface DownloadFileOptions {
  /** Headers to send when the URL belongs to the API origin (e.g. Authorization). */
  headers?: Record<string, string>;
  /** API base URL used to detect when auth headers should be attached. */
  apiBaseUrl?: string;
}

function triggerAnchorDownload(href: string, filename?: string): void {
  const link = document.createElement('a');
  link.href = href;
  if (filename) {
    link.download = filename;
  }
  link.style.display = 'none';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Triggers a download using a pre-signed URL that includes
 * `response-content-disposition=attachment` in the signature (e.g. GCS V4).
 * The filename comes from the server response, not the anchor.
 */
export function downloadFromSignedUrl(url: string): void {
  triggerAnchorDownload(url);
}

function revokeBlobUrlLater(blobUrl: string): void {
  globalThis.window?.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

function getUrlOrigin(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/$/, '');
}

function isApiOrigin(url: string, apiBaseUrl?: string): boolean {
  if (!apiBaseUrl) return false;

  try {
    const normalizedApiBase = normalizeBaseUrl(apiBaseUrl);
    const normalizedUrl = normalizeBaseUrl(url);
    return (
      normalizedUrl === normalizedApiBase || normalizedUrl.startsWith(`${normalizedApiBase}/`)
    );
  } catch {
    return false;
  }
}

function getDocumentOrigin(): string | undefined {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return globalThis.window?.location?.origin;
}

function isSameDocumentOrigin(url: string): boolean {
  const urlOrigin = getUrlOrigin(url);
  const documentOrigin = getDocumentOrigin();
  return Boolean(urlOrigin && documentOrigin && urlOrigin === documentOrigin);
}

async function fetchAsBlob(url: string, headers?: Record<string, string>): Promise<Blob> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'omit',
    ...(headers ? { headers } : {}),
  });

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  return response.blob();
}

export function downloadBlob(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  triggerAnchorDownload(blobUrl, filename);
  revokeBlobUrlLater(blobUrl);
}

/**
 * Downloads a file from a URL. Fetches the resource as a blob when possible
 * so the browser saves the file instead of navigating to it.
 *
 * For cross-origin URLs (e.g. presigned S3), fetch requires CORS on the storage
 * bucket. The `download` attribute on anchors is ignored cross-origin, so fetch
 * must succeed for a forced download.
 */
export async function downloadFileFromUrl(
  url: string,
  filename: string,
  options: DownloadFileOptions = {},
): Promise<void> {
  const { headers, apiBaseUrl } = options;
  const attempts: Array<Record<string, string> | undefined> = [];

  if (isApiOrigin(url, apiBaseUrl) && headers) {
    attempts.push(headers);
  }

  if (!attempts.length) {
    attempts.push(undefined);
  } else {
    attempts.push(undefined);
  }

  for (const attemptHeaders of attempts) {
    try {
      const blob = await fetchAsBlob(url, attemptHeaders);
      downloadBlob(blob, filename);
      return;
    } catch {
      // try next strategy
    }
  }

  if (isSameDocumentOrigin(url)) {
    triggerAnchorDownload(url, filename);
    return;
  }

  throw new Error('Cross-origin download blocked: fetch failed and anchor download is not supported');
}
